import type { SecretInputValue } from '@/components/atoms/SecretInput';
import { type ProviderGroup, SECRET_NAMES, type SecretName } from '@/domain/providerConfig';
import { sameSecret, sameValue } from '@/routes/shared/ProviderConfig/form/formLeaves';
import type { FormFieldValue } from '@/routes/shared/ProviderConfig/form/formValues';

import type { FormDiff } from './formDiff';
import type { PenFormValues, ProviderFormValues } from './formModel';

/** One place both the user and someone else changed. Secrets never appear here. */
export interface FieldConflict {
    /** A form path, e.g. `pens.c101.fields.external_unit_ids`, or `pens.c101` for a whole pen. */
    path: string;
    mine: FormFieldValue | null;
    theirs: FormFieldValue | null;
}

type Values = Record<string, FormFieldValue>;

function mergeValues(
    old: Values,
    fresh: Values,
    current: Values,
    prefix: string,
    conflicts: FieldConflict[]
): Values {
    const merged: Values = { ...fresh };
    for (const key of new Set([...Object.keys(old), ...Object.keys(current)])) {
        if (sameValue(current[key], old[key])) continue;
        if (current[key] === undefined) delete merged[key];
        else merged[key] = current[key];
        if (!sameValue(fresh[key], old[key]) && !sameValue(fresh[key], current[key]))
            conflicts.push({
                path: `${prefix}.${key}`,
                mine: current[key] ?? null,
                theirs: fresh[key] ?? null,
            });
    }
    return merged;
}

/** A typed or cleared secret stays as the user left it; the server never sends one back. */
function mergeSecrets(
    fresh: Record<SecretName, SecretInputValue>,
    current: Record<SecretName, SecretInputValue>
): Record<SecretName, SecretInputValue> {
    const merged = { ...fresh };
    for (const name of SECRET_NAMES)
        if (!sameSecret(current[name], { mode: 'keep' })) merged[name] = current[name];
    return merged;
}

function penEdited(old: PenFormValues, current: PenFormValues): boolean {
    const keys = new Set([...Object.keys(old.fields), ...Object.keys(current.fields)]);
    return (
        current.isActive !== old.isActive ||
        [...keys].some((key) => !sameValue(old.fields[key], current.fields[key])) ||
        Object.values(current.secrets).some((value) => !sameSecret(value, { mode: 'keep' }))
    );
}

function mergePen(
    path: string,
    old: PenFormValues,
    fresh: PenFormValues,
    current: PenFormValues,
    conflicts: FieldConflict[]
): PenFormValues {
    // The save creates this pen from the user's values, so its fields stay shown.
    if (old.connected && !fresh.connected && !current.remove && penEdited(old, current)) {
        conflicts.push({ path, mine: null, theirs: null });
        return { ...current, connected: true, remove: false };
    }
    // A boolean has one other value: when both sides moved it they agree, so it never conflicts.
    const isActive = current.isActive !== old.isActive ? current.isActive : fresh.isActive;
    return {
        // A pen the user is connecting stays connected: the save still creates it.
        connected: fresh.connected || (current.connected && !old.connected),
        isActive,
        // A removal of a row that is already gone has nothing left to remove.
        remove: current.remove && fresh.connected,
        fields: mergeValues(old.fields, fresh.fields, current.fields, `${path}.fields`, conflicts),
        secrets: mergeSecrets(fresh.secrets, current.secrets),
    };
}

/**
 * After a 409, keeps the user's edits on top of the fresh rows: every value the user
 * changed since `oldBase` wins, and is a conflict where the fresh rows changed it too.
 * A pen whose row vanished keeps the user's values and is created again on save; a pen
 * no longer at the site is dropped and reported.
 */
export function mergeAfterConflict(
    oldBase: ProviderFormValues,
    freshBase: ProviderFormValues,
    current: ProviderFormValues
): { values: ProviderFormValues; conflicts: FieldConflict[] } {
    const conflicts: FieldConflict[] = [];
    const connection = mergeValues(
        oldBase.connection,
        freshBase.connection,
        current.connection,
        'connection',
        conflicts
    );
    const pens: Record<string, PenFormValues> = { ...freshBase.pens };
    for (const [key, pen] of Object.entries(current.pens)) {
        const old = oldBase.pens[key];
        const fresh = freshBase.pens[key];
        if (old === undefined) continue;
        if (fresh === undefined) {
            if (penEdited(old, pen))
                conflicts.push({ path: `pens.${key}`, mine: null, theirs: null });
            continue;
        }
        pens[key] = mergePen(`pens.${key}`, old, fresh, pen, conflicts);
    }
    return {
        values: { connection, secrets: mergeSecrets(freshBase.secrets, current.secrets), pens },
        conflicts,
    };
}

function updatedAt(group: ProviderGroup | null): Map<number, number> {
    return new Map((group?.rows ?? []).map((row) => [row.cageId, Date.parse(row.updatedAt)]));
}

/**
 * Whether fresh rows moved under unsaved edits: a row the user has edits for, or any row
 * while a site change is pending, is newer, gone, or (for a pen being created) now there.
 */
export function staleEdits(
    oldGroup: ProviderGroup | null,
    freshGroup: ProviderGroup | null,
    diff: FormDiff
): boolean {
    const before = updatedAt(oldGroup);
    const after = updatedAt(freshGroup);
    const siteChange =
        Object.keys(diff.connection).length > 0 || Object.keys(diff.secrets).length > 0;
    const watched = new Set(Object.keys(diff.pens).map(Number));
    if (siteChange) for (const cageId of [...before.keys(), ...after.keys()]) watched.add(cageId);
    return [...watched].some((cageId) => before.get(cageId) !== after.get(cageId));
}
